import { useCallback, useState } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { apiService } from "../services/api";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "#ffffff",
    fontSize: "16px",
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(226, 114, 91, 0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#E2725B", borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)", fontSize: "15px" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#E2725B" },
};

const selectSx = {
  borderRadius: "16px",
  backgroundColor: "rgba(255,255,255,0.05)",
  color: "#ffffff",
  fontSize: "16px",
  "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
  "&:hover fieldset": { borderColor: "rgba(226, 114, 91, 0.4)" },
  "&.Mui-focused fieldset": { borderColor: "#E2725B", borderWidth: "2px" },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.5)" },
};

export const BR_STATES = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

export interface CardFormData {
  number: string;
  holderName: string;
  expiry: string; // MM/YY
  cvv: string;
  zipCode: string;
  line1: string;
  city: string;
  state: string;
}

export const emptyCardForm: CardFormData = {
  number: "",
  holderName: "",
  expiry: "",
  cvv: "",
  zipCode: "",
  line1: "",
  city: "",
  state: "",
};

const formatCardNumber = (value: string) =>
  value
    .replace(/\D/g, "")
    .slice(0, 16)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const formatExpiry = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
};

// Valida os campos do cartão (e do endereço de cobrança quível requireBilling).
// Retorna a mensagem de erro ou null se válido.
export const validateCardForm = (
  d: CardFormData,
  requireBilling = true,
): string | null => {
  if (d.number.replace(/\s/g, "").length < 13) return "Número do cartão inválido";
  if (!d.holderName.trim()) return "Nome no cartão é obrigatório";
  const [mm, yy] = d.expiry.split("/");
  if (!mm || !yy || parseInt(mm) < 1 || parseInt(mm) > 12) return "Validade inválida";
  if (d.cvv.length < 3) return "CVV inválido";
  if (requireBilling) {
    if (d.zipCode.replace(/\D/g, "").length !== 8) return "CEP inválido";
    if (!d.line1.trim()) return "Endereço de cobrança é obrigatório";
    if (!d.city.trim()) return "Cidade é obrigatória";
    if (!d.state) return "Estado é obrigatório";
  }
  return null;
};

// Tokeniza o cartão direto no Pagar.me (dados brutos nunca passam pelo backend).
export const tokenizeCardForm = (d: CardFormData): Promise<string> => {
  const [mm, yy] = d.expiry.split("/");
  return apiService.tokenizeCard({
    number: d.number.replace(/\s/g, ""),
    holderName: d.holderName.trim(),
    expMonth: parseInt(mm),
    expYear: parseInt(yy.length === 2 ? `20${yy}` : yy),
    cvv: d.cvv,
  });
};

export const billingAddressFromForm = (d: CardFormData) => ({
  line_1: d.line1.trim(),
  zip_code: d.zipCode.replace(/\D/g, ""),
  city: d.city.trim(),
  state: d.state,
});

interface CardFormProps {
  value: CardFormData;
  onChange: (value: CardFormData) => void;
  requireBilling?: boolean;
}

export default function CardForm({
  value,
  onChange,
  requireBilling = true,
}: CardFormProps) {
  const [loadingCep, setLoadingCep] = useState(false);

  const set = (patch: Partial<CardFormData>) => onChange({ ...value, ...patch });

  const fetchAddressByCep = useCallback(
    async (cep: string) => {
      const digits = cep.replace(/\D/g, "");
      if (digits.length !== 8) return;
      setLoadingCep(true);
      try {
        const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
        const data = await res.json();
        if (data.erro) return;
        onChange({
          ...value,
          zipCode: cep,
          city: data.localidade || value.city,
          state: data.uf || value.state,
          line1: data.logradouro || value.line1,
        });
      } catch {
        /* silencioso — não bloqueia o formulário */
      } finally {
        setLoadingCep(false);
      }
    },
    [value, onChange],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        label="Número do cartão"
        value={value.number}
        onChange={(e) => set({ number: formatCardNumber(e.target.value) })}
        placeholder="0000 0000 0000 0000"
        fullWidth
        sx={inputSx}
        inputProps={{ inputMode: "numeric" }}
      />
      <TextField
        label="Nome no cartão"
        value={value.holderName}
        onChange={(e) => set({ holderName: e.target.value.toUpperCase() })}
        fullWidth
        sx={inputSx}
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          label="Validade"
          value={value.expiry}
          onChange={(e) => set({ expiry: formatExpiry(e.target.value) })}
          placeholder="MM/AA"
          fullWidth
          sx={inputSx}
          inputProps={{ inputMode: "numeric" }}
        />
        <TextField
          label="CVV"
          value={value.cvv}
          onChange={(e) => set({ cvv: e.target.value.replace(/\D/g, "").slice(0, 4) })}
          type="password"
          fullWidth
          sx={inputSx}
          inputProps={{ inputMode: "numeric" }}
        />
      </Box>

      {requireBilling && (
        <>
          <TextField
            label="CEP"
            value={value.zipCode}
            onChange={(e) => set({ zipCode: e.target.value })}
            onBlur={(e) => fetchAddressByCep(e.target.value)}
            placeholder="00000-000"
            fullWidth
            sx={inputSx}
            helperText={loadingCep ? "Buscando endereço..." : ""}
            FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
            inputProps={{ inputMode: "numeric" }}
          />
          <TextField
            label="Endereço (rua, número)"
            value={value.line1}
            onChange={(e) => set({ line1: e.target.value })}
            fullWidth
            sx={inputSx}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Cidade"
              value={value.city}
              onChange={(e) => set({ city: e.target.value })}
              fullWidth
              sx={inputSx}
            />
            <FormControl sx={{ minWidth: 110 }}>
              <InputLabel sx={{ color: "rgba(255,255,255,0.6)" }}>UF</InputLabel>
              <Select
                label="UF"
                value={value.state}
                onChange={(e) => set({ state: e.target.value })}
                sx={selectSx}
              >
                {BR_STATES.map((uf) => (
                  <MenuItem key={uf} value={uf}>
                    {uf}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </>
      )}
    </Box>
  );
}
